// Bug 5 fix — extracted release/undo actions so the supabase chain can be
// unit-tested. See audit-reports/investigations/2-bugs-abonnemang-investigation.md.
//
// Why a separate module:
//   1. Pure-function unit tests against a mocked supabase client (no RTL
//      scaffolding for the dialog component).
//   2. Regression guard against re-introducing the UPDATE-tombstone pattern
//      that caused Bug 5.
//
// The shape mirrors how SubscriptionDetailDialog.tsx used to construct the
// chain inline. The dialog now imports performRelease + performUndoRelease.

import type { SupabaseClient } from "@supabase/supabase-js";

export interface PerformReleaseParams {
  subscriptionId: string;
  /** Date in YYYY-MM-DD format (Stockholm local date). */
  releaseDate: string;
  courtId: string;
  /** HH:mm:ss or HH:mm. */
  startTime: string;
  endTime: string;
  organizationId: string;
  releasedBy?: string | null;
  /** Original subscription price in kronor (NUMERIC -- legacy). */
  originalPrice?: number;
}

export interface PerformUndoReleaseParams {
  subscriptionId: string;
  releaseDate: string;
}

/**
 * INSERT a 'released' row into subscription_releases. The supabase response
 * is returned as-is so the caller can inspect `error.code === "23505"`
 * (duplicate-key) and surface a user-facing toast when a stale tombstone
 * still exists (defensive — Bug 5 fix removes the cause).
 */
export async function performRelease(
  supabase: SupabaseClient,
  params: PerformReleaseParams,
): Promise<{ error: { message: string; code?: string } | null }> {
  // Cast `as any` because the generated supabase types may not include
  // public.subscription_releases (it's in the schema but the type-gen run
  // is intermittent). Behavior is unaffected; same pattern as the dialog.
  const { error } = await (supabase
    .from("subscription_releases" as any)
    .insert({
      subscription_id: params.subscriptionId,
      release_date: params.releaseDate,
      court_id: params.courtId,
      start_time: params.startTime,
      end_time: params.endTime,
      organization_id: params.organizationId,
      status: "released",
      released_by: params.releasedBy ?? null,
      original_price: params.originalPrice ?? 0,
    } as any) as any);
  return { error: error as any };
}

/**
 * Bug 5 fix — DELETE the released row (NOT UPDATE status='cancelled').
 *
 * The previous implementation did `.update({ status: "cancelled" })` and
 * left a tombstone row in DB. The UNIQUE constraint on
 * (subscription_id, release_date) then rejected any future re-release
 * INSERT with 23505, surfacing as the misleading "Denna tid är redan
 * ledigställd" toast.
 *
 * DELETE removes the row entirely so the next handleRelease succeeds.
 *
 * The `.eq("status", "released")` filter is a safety guard so we never
 * delete a row that was already 'rebooked' (a customer booked over the
 * released slot via the eligible-booking-types flow in AdminScheduleGrid).
 */
export async function performUndoRelease(
  supabase: SupabaseClient,
  params: PerformUndoReleaseParams,
): Promise<{ error: { message: string } | null }> {
  const { error } = await (supabase
    .from("subscription_releases" as any)
    .delete()
    .eq("subscription_id", params.subscriptionId)
    .eq("release_date", params.releaseDate)
    .eq("status", "released") as any);
  return { error: error as any };
}
